/**
 * Live activity, work-toggle, and work-group rows (ticket 3525, upstream pin
 * e3e7cc3f, MIT), ported from upstream `MessagesTimeline.tsx`. Forge deltas:
 * tool-group view state rides TimelineRowContext, scroll fades are token
 * overlays instead of upstream's class helper, and the spawn row lives in its
 * own module to keep the import graph acyclic.
 *
 * @module TimelineLiveRows
 */

import {
  workEntryDisplayIndicatesToolFailure,
  workEntryIndicatesToolNeutralStatus,
  type WorkLogEntry,
} from "../../session-logic";
import { liveWorkEntryLabel } from "./MessagesTimeline.labels";
import { getQuestionAnswerPreview } from "../../workLogUserInput";
import { use } from "react";
import { cn } from "~/lib/utils";
import { TimelineRowCtx } from "./TimelineRowContext";
import { SimpleWorkEntryRow } from "./TimelineWorkEntryRows";
import {
  ToolActivityIconView,
  toolGroupSummaryIconName,
  workEntryIconName,
} from "./TimelineWorkEntryIcons";
import { LiveActivityRow } from "./TimelineLiveActivityRow";
import { AgentSpawnRow } from "./TimelineSpawnRow";
import { ExpandedWorkGroupEntries } from "./TimelineExpandedWorkGroup";
import type { MessagesTimelineRow } from "./MessagesTimeline.rowTypes";

export function LiveWorkEntryTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "work-live" }>;
}) {
  const ctx = use(TimelineRowCtx);
  if (row.entry.agentSpawn) {
    return (
      <AgentSpawnRow
        workEntry={row.entry}
        active={row.active}
        onToggleEntry={(collapsed) => ctx.onToggleWorkEntry(row.id, collapsed)}
      />
    );
  }
  const label = liveWorkEntryLabel(row.entry, ctx.workspaceRoot, row.active);
  const failed = workEntryDisplayIndicatesToolFailure(row.entry);

  return (
    <button
      type="button"
      className="group/live-work flex min-h-6 w-full max-w-full cursor-pointer items-center rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      aria-label={failed ? `${label}, tool call failed` : undefined}
      aria-expanded={row.expanded}
      onClick={() => ctx.onToggleWorkGroup(row.groupId)}
    >
      <LiveActivityRow
        label={
          row.entry.questionAnswer ? (
            <span className="flex min-w-0 gap-1.5">
              <span className="shrink-0">{label}</span>
              <span
                className={cn(
                  "truncate",
                  !row.expanded ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {getQuestionAnswerPreview(row.entry.questionAnswer)}
              </span>
            </span>
          ) : (
            label
          )
        }
        iconName={workEntryIconName(row.entry)}
        toolIcon={row.entry.toolIcon ?? row.entry.toolSource?.icon}
        failed={failed}
        active={row.active}
      />
    </button>
  );
}

export function WorkGroupToggleTimelineRow({
  row,
}: {
  row: Extract<MessagesTimelineRow, { kind: "work-toggle" }>;
}) {
  const ctx = use(TimelineRowCtx);
  return (
    <button
      type="button"
      className="group/tool-group group/timeline-row relative flex min-h-6 w-full cursor-pointer items-center gap-1.5 rounded-md px-0.5 py-0.5 text-left text-sm leading-relaxed transition-colors duration-150 hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      aria-label={row.hasFailure ? `${row.summary}, tool call failed` : undefined}
      aria-expanded={row.expanded}
      onClick={() => ctx.onToggleWorkGroup(row.groupId)}
    >
      <span className="flex size-6 shrink-0 items-center justify-center text-icon-muted">
        <ToolActivityIconView
          icon={row.toolIcon}
          fallbackName={
            row.summaryToolIcon ?? row.toolSurface ?? toolGroupSummaryIconName(row.summaryKind)
          }
          className="size-4 shrink-0 stroke-2"
          muted
        />
      </span>
      <span className="min-w-0 flex-1 truncate text-secondary-label">{row.summary}</span>
    </button>
  );
}

/** Renders standalone activity or one bounded, virtualized expanded tool group. */
export function WorkGroupSection({
  anchorKey,
  groupedEntries,
  isExpandedToolGroup,
  displayLabel,
}: {
  anchorKey: string;
  groupedEntries: WorkLogEntry[];
  isExpandedToolGroup: boolean;
  displayLabel?: string | undefined;
}) {
  const ctx = use(TimelineRowCtx);
  const { workspaceRoot } = ctx;
  const nonEmptyEntries = groupedEntries.filter((entry) =>
    workEntryIsVisibleInGroupEntry(entry, isExpandedToolGroup),
  );

  if (nonEmptyEntries.length === 0) return null;
  if (isExpandedToolGroup) {
    return (
      <ExpandedWorkGroupEntries
        key={`${ctx.routeThreadKey}:${anchorKey}`}
        anchorKey={anchorKey}
        entries={nonEmptyEntries}
        workspaceRoot={workspaceRoot}
        viewState={ctx.workGroupViewState}
      />
    );
  }

  return (
    <section className="-mx-1 space-y-0.5 px-1 py-0.5" aria-label="Activity">
      <div className="space-y-px">
        {nonEmptyEntries.map((workEntry) => (
          <SimpleWorkEntryRow
            key={workEntry.id}
            workEntry={workEntry}
            workspaceRoot={workspaceRoot}
            isExpandedToolGroupEntry={false}
            displayLabel={displayLabel}
            onToggleEntry={(collapsed) => ctx.onToggleWorkEntry(anchorKey, collapsed)}
          />
        ))}
      </div>
    </section>
  );
}

function workEntryIsVisibleInGroupEntry(
  entry: WorkLogEntry,
  isExpandedToolGroup: boolean,
): boolean {
  return (
    (isExpandedToolGroup &&
      (entry.toolLifecycleStatus === "inProgress" ||
        entry.sourceActivityKind === "task.progress")) ||
    entry.githubConfirmation !== undefined ||
    entry.githubActionRequired !== undefined ||
    !workEntryIndicatesToolNeutralStatus(entry)
  );
}
